import { FC, useState } from "react"
import { Plus } from "lucide-react"
import { ConnectorDataType } from "../../../../../../../types"
import { FieldDefinition } from "../../../../../../constants/connector-field-types"
import ConnectorFormButton from "../../components/ConnectorFormButton"

interface AddFieldSectionProps {
  isIncoming: boolean
  dataType: ConnectorDataType
  newBbFieldToAdd: string
  newCrmFieldToAdd: string
  availableFields: FieldDefinition[]
  onBbFieldChange: (value: string) => void
  onCrmFieldChange: (value: string) => void
  onAdd: () => void
  onAddCustom?: (customFieldPath: string) => void
}

const AddFieldSection: FC<AddFieldSectionProps> = ({
  isIncoming,
  dataType,
  newBbFieldToAdd,
  newCrmFieldToAdd,
  availableFields,
  onBbFieldChange,
  onCrmFieldChange,
  onAdd,
  onAddCustom
}) => {
  const [customFieldValue, setCustomFieldValue] = useState("")
  const isCustomMode = newBbFieldToAdd === "__custom__"

  // Determine namespace based on data type
  const customFieldNamespace = dataType === "lead" ? "details" : "quote_info"
  const customOptionLabel = `→ Custom (${customFieldNamespace}) Field...`

  const handleBbFieldSelect = (value: string) => {
    if (value === "__custom__") {
      onBbFieldChange("__custom__")
      setCustomFieldValue("")
    } else {
      onBbFieldChange(value)
      setCustomFieldValue("")
    }
  }

  const handleAdd = () => {
    if (isCustomMode && customFieldValue && onAddCustom) {
      // Prepend namespace and add via custom handler
      const fullFieldPath = `${customFieldNamespace}.${customFieldValue}`
      onAddCustom(fullFieldPath)
      setCustomFieldValue("")
      onBbFieldChange("") // Reset dropdown
    } else {
      onAdd()
    }
  }

  const canAdd = isIncoming
    ? newCrmFieldToAdd && (isCustomMode ? customFieldValue : newBbFieldToAdd)
    : isCustomMode
    ? customFieldValue
    : newBbFieldToAdd
  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        {isIncoming ? (
          <>
            <input
              type="text"
              value={newCrmFieldToAdd}
              onChange={e => onCrmFieldChange(e.target.value)}
              placeholder="CRM field (e.g., homebuyer.email)"
              className="px-3 py-2 border border-gray-300 rounded-md text-sm flex-1 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={newBbFieldToAdd}
              onChange={e => handleBbFieldSelect(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm flex-1"
            >
              <option value="">Select BB field...</option>
              {availableFields.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label} {option.type === "select" ? "(select)" : ""}
                </option>
              ))}
              <option value="__custom__">{customOptionLabel}</option>
            </select>
          </>
        ) : (
          <select
            value={newBbFieldToAdd}
            onChange={e => handleBbFieldSelect(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm flex-1"
          >
            <option value="">Select BB field to add...</option>
            {availableFields.map(option => (
              <option key={option.value} value={option.value}>
                {option.label} {option.type === "select" ? "(select)" : ""}
              </option>
            ))}
            <option value="__custom__">{customOptionLabel}</option>
          </select>
        )}
        <ConnectorFormButton
          onClick={handleAdd}
          disabled={!canAdd}
          className="flex items-center"
        >
          <Plus size={16} className="mr-1" />
          Add Field
        </ConnectorFormButton>
      </div>

      {isCustomMode && (
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600 font-mono">
            {customFieldNamespace}.
          </span>
          <input
            type="text"
            value={customFieldValue}
            onChange={e => setCustomFieldValue(e.target.value)}
            placeholder={`field_name (e.g., utm_campaign, custom_field)`}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}
    </div>
  )
}

export default AddFieldSection
